import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createEditJobOptionsQuery } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { filterMaterialGroupsByDesignCode } from "@/utils/materialFilters";

const strongbackSchema = z.object({
  strongbacks: z.string().min(1, { message: "Strongbacks is required" }),
  strongbacks_stud_bolts: z.string().min(1, { message: "Strongbacks stud bolts is required" }),
  match_strongback_to_enclosure: z.boolean().optional(),
});

export type StrongbackSchema = z.infer<typeof strongbackSchema>;

interface StrongbacksFormProps {
  onSave: (data: StrongbackSchema) => void;
  defaultValues: StrongbackSchema;
}

export function StrongbacksForm({ onSave, defaultValues }: StrongbacksFormProps) {
  const designCode = useCUMechanicalJobStore((state) => state.sections.jobDetails.data.design_code);
  const cuOptionsQuery = useQuery({
    ...createEditJobOptionsQuery,
    select(response) {
      return {
        strongbackOptions: response.strongbacks,
        strongbackStudBoltOptions: response.strongbacks_stud_bolts,
      };
    },
  });
  const filteredStrongbackOptions = useMemo(
    () => filterMaterialGroupsByDesignCode(cuOptionsQuery.data?.strongbackOptions, designCode),
    [cuOptionsQuery.data?.strongbackOptions, designCode]
  );
  const filteredStrongbackStudBoltOptions = useMemo(
    () => filterMaterialGroupsByDesignCode(cuOptionsQuery.data?.strongbackStudBoltOptions, designCode),
    [cuOptionsQuery.data?.strongbackStudBoltOptions, designCode]
  );

  const form = useForm<StrongbackSchema>({
    resolver: zodResolver(strongbackSchema),
    values: defaultValues,
  });

  const { handleSubmit } = form;

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSave)} className="flex flex-col gap-2">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 basis-full md:basis-56">
            <FormField
              control={form.control}
              name="strongbacks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Strongbacks</FormLabel>
                  <FormControl>
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                        <SelectValue placeholder="Select strongback type" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredStrongbackOptions?.map((group) => (
                          <SelectGroup key={group.group}>
                            <SelectLabel>{group.group}</SelectLabel>
                            {group.options.map((option) => (
                              <SelectItem key={option.id} value={String(option.id)}>
                                {option.material} - {option.type}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex-1 basis-full md:basis-56">
            <FormField
              control={form.control}
              name="strongbacks_stud_bolts"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Strongbacks stud bolts</FormLabel>
                  <FormControl>
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                        <SelectValue placeholder="Select stud bolt type" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredStrongbackStudBoltOptions?.map((group) => (
                          <SelectGroup key={group.group}>
                            <SelectLabel>{group.group}</SelectLabel>
                            {group.options.map((option) => (
                              <SelectItem key={option.id} value={String(option.id)}>
                                {option.material} - {option.type}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <Button type="submit" className="self-end">
          Save
        </Button>
      </form>
    </Form>
  );
}
